/*
 * The Salford Club — builder behaviour
 * - Selection / quantity / running subscription total
 * - Adds every selected bottle as an Appstle subscription line via the native
 *   Shopify AJAX cart (/cart/add.js) using each product's resolved selling_plan
 * - Opens the existing Dawn cart drawer if present, otherwise falls back to /cart
 * - Re-initialises cleanly on Theme Editor section load (no duplicate listeners)
 * - Emits analytics events on window.dataLayer when present
 *
 * All selling-plan / variant / price data is provided by Liquid as data-*
 * attributes on each card, so no numeric plan IDs are hard-coded here.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-salford-club-builder]';

  function formatMoney(cents, format) {
    // Prefer the theme's own formatter when available for currency parity.
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, format);
      }
    } catch (e) {}
    var value = (cents / 100).toFixed(2);
    return (format && format.indexOf('amount') === -1 ? '£' : '£') + value;
  }

  function emit(name, detail) {
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, detail || {}));
      }
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('salford-club:' + name, { detail: detail || {} }));
    } catch (e) {}
  }

  function Builder(root) {
    this.root = root;
    this.moneyFormat = root.getAttribute('data-money-format') || '';
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-sc-card]'));
    this.summaryLines = root.querySelector('[data-sc-summary-lines]');
    this.summaryEmpty = root.querySelector('[data-sc-summary-empty]');
    this.totalEl = root.querySelector('[data-sc-total]');
    this.submitBtn = root.querySelector('[data-sc-submit]');
    this.errorEl = root.querySelector('[data-sc-error]');
    this.summaryPanel = root.querySelector('[data-sc-summary]');
    this.pickerOpenBtn = root.querySelector('[data-sc-picker-open]');
    this.pickerDrawer = root.querySelector('[data-sc-picker-drawer]');
    this.pickerCloseBtns = Array.prototype.slice.call(root.querySelectorAll('[data-sc-picker-close]'));
    this.pickerTitle = root.querySelector('[data-sc-picker-title]');
    this.pickerMeta = root.querySelector('[data-sc-picker-meta]');
    this.pickerThumb = root.querySelector('[data-sc-picker-thumb]');
    this.pickerSelection = root.querySelector('[data-sc-picker-selection]');
    this.pickerPlaceholder = this.pickerTitle ? this.pickerTitle.textContent : 'Select your bottles';
    this.pickerOpenLabel = this.pickerMeta ? this.pickerMeta.textContent : 'Open bottle picker';
    this.messages = {
      empty: root.getAttribute('data-msg-empty') || 'Select at least one bottle to start your Club.',
      noPlan: root.getAttribute('data-msg-no-plan') || 'This bottle is not available on the Club plan right now.',
      unavailable: root.getAttribute('data-msg-unavailable') || 'This bottle is currently unavailable.',
      addFail: root.getAttribute('data-msg-add-fail') || 'Something went wrong adding your bottles. Please try again.'
    };
    this.state = {}; // variantId -> { quantity, sellingPlan, price, title }
    this.onSectionUnload = this.onSectionUnload.bind(this);
    this.onPickerKeydown = this.onPickerKeydown.bind(this);
    this.bind();
    this.render();
  }

  Builder.prototype.bind = function () {
    var self = this;

    this.cards.forEach(function (card) {
      var variantId = card.getAttribute('data-variant-id');
      var toggle = card.querySelector('[data-sc-toggle]');
      var qtyInput = card.querySelector('[data-sc-qty-input]');
      var minus = card.querySelector('[data-sc-qty-minus]');
      var plus = card.querySelector('[data-sc-qty-plus]');

      if (toggle) {
        toggle.addEventListener('click', function () {
          self.toggle(card);
        });
      }
      if (minus) {
        minus.addEventListener('click', function () {
          self.step(card, -1);
        });
      }
      if (plus) {
        plus.addEventListener('click', function () {
          self.step(card, 1);
        });
      }
      if (qtyInput) {
        qtyInput.addEventListener('change', function () {
          self.setQty(card, parseInt(qtyInput.value, 10));
        });
      }
    });

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', function () {
        self.submit();
      });
    }

    if (this.pickerOpenBtn) {
      this.pickerOpenBtn.addEventListener('click', function () {
        self.openPicker();
      });
    }

    this.pickerCloseBtns.forEach(function (button) {
      button.addEventListener('click', function () {
        self.closePicker();
      });
    });

    // Clean teardown if the editor reloads this section.
    document.addEventListener('shopify:section:unload', this.onSectionUnload);
  };

  Builder.prototype.onSectionUnload = function (evt) {
    if (evt && evt.target && evt.target.contains(this.root)) {
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      document.removeEventListener('keydown', this.onPickerKeydown);
      document.documentElement.classList.remove('salford-club-picker-open');
      this.root.__salfordClubBound = false;
    }
  };

  Builder.prototype.openPicker = function () {
    if (!this.pickerDrawer) return;
    this.pickerDrawer.classList.add('is-open');
    this.pickerDrawer.setAttribute('aria-hidden', 'false');
    if (this.pickerOpenBtn) this.pickerOpenBtn.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('salford-club-picker-open');
    document.addEventListener('keydown', this.onPickerKeydown);
    var closeBtn = this.pickerDrawer.querySelector('.salford-club__picker-close');
    if (closeBtn) closeBtn.focus();
  };

  Builder.prototype.closePicker = function () {
    if (!this.pickerDrawer) return;
    this.pickerDrawer.classList.remove('is-open');
    this.pickerDrawer.setAttribute('aria-hidden', 'true');
    if (this.pickerOpenBtn) {
      this.pickerOpenBtn.setAttribute('aria-expanded', 'false');
      this.pickerOpenBtn.focus();
    }
    document.documentElement.classList.remove('salford-club-picker-open');
    document.removeEventListener('keydown', this.onPickerKeydown);
  };

  Builder.prototype.onPickerKeydown = function (evt) {
    if (evt && evt.key === 'Escape') {
      this.closePicker();
    }
  };

  Builder.prototype.isSelectable = function (card) {
    return card.getAttribute('data-available') === 'true' &&
           !!card.getAttribute('data-selling-plan');
  };

  Builder.prototype.toggle = function (card) {
    var variantId = card.getAttribute('data-variant-id');
    if (!this.isSelectable(card)) {
      this.showCardFlag(card);
      return;
    }
    if (this.state[variantId]) {
      delete this.state[variantId];
      card.classList.remove('is-selected');
      this.setToggleLabel(card, false);
      emit('product_removed', { variant_id: variantId });
    } else {
      var qtyInput = card.querySelector('[data-sc-qty-input]');
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      this.state[variantId] = {
        quantity: qty,
        sellingPlan: card.getAttribute('data-selling-plan'),
        price: parseInt(card.getAttribute('data-club-price'), 10) || 0,
        title: card.getAttribute('data-title') || ''
      };
      card.classList.add('is-selected');
      this.setToggleLabel(card, true);
      emit('product_selected', {
        variant_id: variantId,
        selling_plan: this.state[variantId].sellingPlan,
        quantity: qty
      });
    }
    this.render();
  };

  Builder.prototype.setToggleLabel = function (card, selected) {
    var toggle = card.querySelector('[data-sc-toggle]');
    if (!toggle) return;
    var add = toggle.getAttribute('data-label-add') || 'Add to Club';
    var remove = toggle.getAttribute('data-label-remove') || 'Remove';
    toggle.textContent = selected ? remove : add;
    toggle.setAttribute('aria-pressed', selected ? 'true' : 'false');
  };

  Builder.prototype.step = function (card, delta) {
    var qtyInput = card.querySelector('[data-sc-qty-input]');
    if (!qtyInput) return;
    var next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + delta);
    this.setQty(card, next);
  };

  Builder.prototype.setQty = function (card, qty) {
    var qtyInput = card.querySelector('[data-sc-qty-input]');
    var variantId = card.getAttribute('data-variant-id');
    qty = Math.max(1, qty || 1);
    if (qtyInput) qtyInput.value = qty;
    if (this.state[variantId]) {
      this.state[variantId].quantity = qty;
      emit('quantity_changed', { variant_id: variantId, quantity: qty });
      this.render();
    }
  };

  Builder.prototype.showCardFlag = function (card) {
    var flag = card.querySelector('[data-sc-flag]');
    if (flag) flag.hidden = false;
  };

  Builder.prototype.selectedVariants = function () {
    return Object.keys(this.state);
  };

  Builder.prototype.cardByVariant = function (variantId) {
    for (var i = 0; i < this.cards.length; i += 1) {
      if (this.cards[i].getAttribute('data-variant-id') === variantId) return this.cards[i];
    }
    return null;
  };

  Builder.prototype.renderPicker = function (ids) {
    if (!this.pickerTitle && !this.pickerMeta && !this.pickerSelection && !this.pickerThumb) return;

    var selected = ids.map(function (id) {
      return this.state[id];
    }, this);

    if (this.pickerTitle) {
      this.pickerTitle.textContent = selected.length
        ? selected.length + (selected.length === 1 ? ' bottle selected' : ' bottles selected')
        : this.pickerPlaceholder;
    }

    if (this.pickerMeta) {
      this.pickerMeta.textContent = selected.length
        ? selected.map(function (line) {
            return line.title + (line.quantity > 1 ? ' x ' + line.quantity : '');
          }).slice(0, 3).join(', ') + (selected.length > 3 ? ' +' + (selected.length - 3) + ' more' : '')
        : this.pickerOpenLabel;
    }

    if (this.pickerSelection) {
      this.pickerSelection.innerHTML = '';
      selected.forEach(function (line) {
        var item = document.createElement('li');
        item.textContent = line.title + (line.quantity > 1 ? ' x ' + line.quantity : '');
        this.pickerSelection.appendChild(item);
      }, this);
      this.pickerSelection.hidden = selected.length === 0;
    }

    if (this.pickerThumb) {
      this.pickerThumb.innerHTML = '';
      this.pickerThumb.classList.remove('is-filled');
      if (ids.length) {
        var card = this.cardByVariant(ids[0]);
        var source = card ? card.querySelector('.salford-club__card-media img') : null;
        if (source) {
          var img = document.createElement('img');
          img.src = source.currentSrc || source.src;
          img.alt = '';
          this.pickerThumb.appendChild(img);
          this.pickerThumb.classList.add('is-filled');
        }
      }
    }
  };

  Builder.prototype.render = function () {
    var self = this;
    var ids = this.selectedVariants();
    var total = 0;

    if (this.summaryLines) {
      this.summaryLines.innerHTML = '';
      ids.forEach(function (id) {
        var line = self.state[id];
        total += line.price * line.quantity;
        var li = document.createElement('li');
        var name = document.createElement('span');
        name.textContent = line.title + (line.quantity > 1 ? ' × ' + line.quantity : '');
        var amount = document.createElement('span');
        amount.textContent = formatMoney(line.price * line.quantity, self.moneyFormat);
        li.appendChild(name);
        li.appendChild(amount);
        self.summaryLines.appendChild(li);
      });
    }

    if (this.summaryEmpty) this.summaryEmpty.hidden = ids.length > 0;
    if (this.summaryLines) this.summaryLines.hidden = ids.length === 0;
    if (this.totalEl) this.totalEl.textContent = formatMoney(total, this.moneyFormat);

    if (this.submitBtn) {
      this.submitBtn.disabled = ids.length === 0;
      this.submitBtn.classList.toggle('is-disabled', ids.length === 0);
    }

    if (this.summaryPanel) {
      this.summaryPanel.classList.toggle('is-sticky-mobile', ids.length > 0);
    }

    this.renderPicker(ids);
    this.clearError();
  };

  Builder.prototype.showError = function (msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
    this.errorEl.setAttribute('role', 'alert');
  };

  Builder.prototype.clearError = function () {
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  };

  Builder.prototype.submit = function () {
    var self = this;
    var ids = this.selectedVariants();
    if (!ids.length) {
      this.showError(this.messages.empty);
      return;
    }

    var items = ids.map(function (id) {
      var line = self.state[id];
      var item = { id: parseInt(id, 10), quantity: line.quantity };
      if (line.sellingPlan) item.selling_plan = parseInt(line.sellingPlan, 10);
      return item;
    });

    var missingPlan = items.some(function (i) { return !i.selling_plan; });
    if (missingPlan) {
      this.showError(this.messages.noPlan);
      return;
    }

    this.setBusy(true);
    emit('join_clicked', { line_count: items.length });

    var sectionsToRender = this.cartSections();

    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items, sections: sectionsToRender, sections_url: window.location.pathname })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          self.showError((result.data && result.data.description) || self.messages.addFail);
          self.setBusy(false);
          return;
        }
        emit('subscription_added', {
          line_count: items.length,
          items: items
        });
        self.openCart(result.data);
        self.setBusy(false);
      })
      .catch(function () {
        self.showError(self.messages.addFail);
        self.setBusy(false);
      });
  };

  Builder.prototype.setBusy = function (busy) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = busy;
    this.submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) {
      this.submitBtn.dataset.idleLabel = this.submitBtn.dataset.idleLabel || this.submitBtn.textContent;
      this.submitBtn.textContent = this.submitBtn.getAttribute('data-busy-label') || 'Adding…';
    } else if (this.submitBtn.dataset.idleLabel) {
      this.submitBtn.textContent = this.submitBtn.dataset.idleLabel;
    }
  };

  Builder.prototype.cartSections = function () {
    // Ask the add request to re-render the drawer + bubble so Dawn can repaint.
    var sections = [];
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.getAttribute('id')) sections.push(drawer.getAttribute('id'));
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) sections.push('cart-icon-bubble');
    if (!sections.length) sections.push('cart-drawer', 'cart-icon-bubble');
    return sections;
  };

  Builder.prototype.openCart = function (addData) {
    // Prefer Dawn's cart-drawer API; degrade gracefully to the cart page.
    var drawer = document.querySelector('cart-drawer');
    try {
      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.classList.remove('is-empty');
        drawer.renderContents(addData);
        if (typeof drawer.open === 'function') drawer.open();
        return;
      }
      // Some themes expose a notification component instead.
      var note = document.querySelector('cart-notification');
      if (note && typeof note.renderContents === 'function') {
        note.renderContents(addData);
        return;
      }
      if (window.Shopify && window.Shopify.publish && window.Shopify.PUB_SUB_EVENTS) {
        window.Shopify.publish(window.Shopify.PUB_SUB_EVENTS.cartUpdate, { source: 'salford-club' });
      }
    } catch (e) {}
    // Fallback: go to the cart so the customer always sees their subscription.
    window.location = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__salfordClubBound) return;
      root.__salfordClubBound = true;
      new Builder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme Editor: (re)initialise only the reloaded section.
  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target);
  });
})();
